/**
 * Facial-recognition SECOND FACTOR. Sits between Firebase auth and the app
 * shell: by the time this renders, email + password have already succeeded
 * (the first factor). It holds an authenticated officer on the face step until
 * their live capture matches the face the superadmin enrolled for them.
 *
 * Enrollment is admin-driven (the superadmin attaches a photo when creating
 * the officer), so there is no self-service enrollment here. Matching happens
 * server-side (/me/face/verify) — the reference biometric never leaves the
 * server; only the live capture is sent.
 *
 * The "passed" flag is scoped to the browser session (sessionStorage), so each
 * new session re-challenges.
 */

import { signOut } from "firebase/auth";
import { useEffect, useState, type ReactNode } from "react";

import { ApiError } from "../api/client";
import { auth } from "../firebase.config";
import { FaceScan } from "./FaceScan";
import { toArray } from "./face";
import { FaceApi } from "./faceApi";

const sessionKey = (uid: string) => `face2fa:${uid}`;

type Phase =
  | { kind: "checking" }
  | { kind: "verify"; error?: string } // showing the camera
  | { kind: "blocked"; message: string } // camera/model/network failure
  | { kind: "passed" };

export function SecondFactorGate({ children }: { children: ReactNode }) {
  const user = auth.currentUser;
  const uid = user?.uid ?? null;

  const [phase, setPhase] = useState<Phase>(() =>
    uid && sessionStorage.getItem(sessionKey(uid)) === "ok"
      ? { kind: "passed" }
      : { kind: "checking" },
  );
  // Bumped to force a fresh FaceScan (restarts the camera) on retry.
  const [scanNonce, setScanNonce] = useState(0);

  // Ask the server whether this officer has a face on file. No face → no
  // second factor (the superadmin decides who gets one).
  useEffect(() => {
    if (!uid || phase.kind !== "checking") return;
    let cancelled = false;
    FaceApi.status()
      .then(({ enrolled }) => {
        if (cancelled) return;
        if (enrolled) setPhase({ kind: "verify" });
        else markPassed();
      })
      .catch((err) => {
        if (cancelled) return;
        setPhase({ kind: "blocked", message: describe(err) });
      });
    return () => {
      cancelled = true;
    };
    // markPassed/describe are stable for this effect's purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, phase.kind]);

  function markPassed() {
    if (uid) sessionStorage.setItem(sessionKey(uid), "ok");
    setPhase({ kind: "passed" });
  }

  async function onCapture(descriptor: Float32Array) {
    setPhase({ kind: "verify" }); // clear any prior inline error
    try {
      const { verified } = await FaceApi.verify(toArray(descriptor));
      if (verified) markPassed();
      else setPhase({ kind: "verify", error: "Face not recognized. Try again." });
    } catch (err) {
      setPhase({ kind: "verify", error: describe(err) });
    }
  }

  // FaceScan calls this with a reason on camera/model failure, or with no
  // reason when the officer taps the "Sign out" control.
  function onCancel(reason?: string) {
    if (reason) setPhase({ kind: "blocked", message: reason });
    else void signOut(auth);
  }

  function retry() {
    setScanNonce((n) => n + 1);
    setPhase({ kind: "verify" });
  }

  // Routing only mounts this for an authenticated user; guard defensively.
  if (!user || phase.kind === "passed") return <>{children}</>;

  return (
    <div className="portal-page">
      <div className="portal-form">
        <div className="portal-brand">
          <div className="portal-mark" aria-hidden>◆</div>
          <h1>Campaign Intelligence</h1>
          <p className="portal-sub">Second step — confirm it&rsquo;s you</p>
        </div>

        {phase.kind === "checking" && <p className="face-scan-status">Preparing verification…</p>}

        {phase.kind === "verify" && (
          <>
            <FaceScan
              key={scanNonce}
              title="Verify your face"
              onCapture={onCapture}
              onCancel={onCancel}
              cancelLabel="Sign out"
            />
            {phase.error && (
              <p role="alert" style={{ color: "#b14444", fontSize: 13, margin: 0, textAlign: "center" }}>
                {phase.error}
              </p>
            )}
          </>
        )}

        {phase.kind === "blocked" && (
          <div className="face-scan">
            <p role="alert" className="face-scan-status" style={{ color: "#b14444" }}>
              {phase.message}
            </p>
            <button type="button" className="face-scan-capture" onClick={retry}>
              Try again
            </button>
            <button type="button" className="face-scan-cancel" onClick={() => void signOut(auth)}>
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Verification failed. Please try again.";
}
